import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', service: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d5okg7vecauet1j8bgbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-teal-600 text-sm font-semibold tracking-widest uppercase mb-3">Get In Touch</p>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold text-teal-950 mb-4">Contact Dr. Leder's Office</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">Reach out to schedule your free 10-minute consultation or ask any questions about our functional medicine services.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Info */}
          <div className="space-y-8">
            <div>
              <h3 className="font-heading text-xl font-semibold text-teal-950 mb-5">Office Information</h3>
              <div className="space-y-5">
                {[
                  { icon: 'ri-map-pin-line', label: 'Address', content: <>A Better Alternative Medical Center<br />235 Prospect Avenue, Suite LB<br />Hackensack, NJ 07601</> },
                  { icon: 'ri-phone-line', label: 'In-Office (Tue/Thu/Sat)', content: <a href="tel:2015251155" className="text-teal-700 font-semibold hover:text-teal-800 cursor-pointer">(201) 525-1155</a> },
                  { icon: 'ri-phone-line', label: 'Direct (Sun/Mon/Wed/Fri)', content: <a href="tel:5512988799" className="text-teal-700 font-semibold hover:text-teal-800 cursor-pointer">(551) 298-8799</a> },
                  { icon: 'ri-mail-line', label: 'Email', content: <a href="mailto:a.better.alternative.medical@gmail.com" className="text-teal-700 hover:text-teal-800 cursor-pointer text-sm break-all">a.better.alternative.medical@gmail.com</a> },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0">
                      <i className={`${item.icon} text-teal-700 text-xl`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-teal-500 uppercase tracking-widest mb-1">{item.label}</p>
                      <div className="text-gray-800 text-sm leading-relaxed">{item.content}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-teal-50 border border-teal-100 rounded-lg p-6">
              <h4 className="font-heading font-semibold text-teal-900 mb-3">Office Hours</h4>
              <div className="space-y-2 text-sm">
                {[
                  ['Tuesday, Thursday, Saturday', 'In-Office Visits'],
                  ['Sunday, Monday, Wednesday, Friday', 'Direct Consultations'],
                ].map(([days, type]) => (
                  <div key={days} className="flex justify-between items-center py-2 border-b border-teal-100 last:border-0">
                    <span className="text-teal-900 font-medium">{days}</span>
                    <span className="text-teal-600 text-xs">{type}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <a href="http://facebook.com/altmedicalcenter" target="_blank" rel="noopener noreferrer" className="w-11 h-11 flex items-center justify-center bg-teal-100 hover:bg-teal-200 rounded-md transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-teal-700 text-xl"></i>
              </a>
              <a href="https://www.linkedin.com/in/robin-ellen-leder-md-9380a9317/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 flex items-center justify-center bg-teal-100 hover:bg-teal-200 rounded-md transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-teal-700 text-xl"></i>
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="bg-teal-50 border border-teal-100 rounded-lg p-8">
            <h3 className="font-heading text-xl font-semibold text-teal-950 mb-6">Request a Free Consultation</h3>
            <form id="contact-main-form" onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-teal-800 uppercase tracking-wider mb-1.5">Full Name *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Your full name" className="w-full px-4 py-3 border border-teal-200 rounded-md bg-white text-sm focus:outline-none focus:border-teal-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-teal-800 uppercase tracking-wider mb-1.5">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(xxx) xxx-xxxx" className="w-full px-4 py-3 border border-teal-200 rounded-md bg-white text-sm focus:outline-none focus:border-teal-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-teal-800 uppercase tracking-wider mb-1.5">Email Address *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" className="w-full px-4 py-3 border border-teal-200 rounded-md bg-white text-sm focus:outline-none focus:border-teal-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-teal-800 uppercase tracking-wider mb-1.5">Service of Interest</label>
                <select name="service" value={formData.service} onChange={handleChange} className="w-full px-4 py-3 border border-teal-200 rounded-md bg-white text-sm focus:outline-none focus:border-teal-500">
                  <option value="">Select a program...</option>
                  <option>Thyroid &amp; Adrenal Solutions</option>
                  <option>Bio-Identical Hormone Therapy</option>
                  <option>Chelation &amp; IV Therapy</option>
                  <option>Alternatives to Psychiatric Medications</option>
                  <option>Sleep Solutions</option>
                  <option>Weight Loss &amp; Metabolic Programs</option>
                  <option>Specialized Testing</option>
                  <option>Total Health Makeover</option>
                  <option>Women's &amp; Men's Wellness</option>
                  <option>Couples Counseling</option>
                  <option>Medical Marijuana Certification</option>
                  <option>General Inquiry</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-teal-800 uppercase tracking-wider mb-1.5">Your Health Concerns *</label>
                <textarea name="message" value={formData.message} onChange={handleChange} required maxLength={500} rows={4} placeholder="Briefly describe your main health concerns or questions..." className="w-full px-4 py-3 border border-teal-200 rounded-md bg-white text-sm focus:outline-none focus:border-teal-500 resize-none"></textarea>
                <p className="text-xs text-teal-500 mt-1">{formData.message.length}/500 characters</p>
              </div>
              <button type="submit" disabled={isSubmitting} className="w-full bg-teal-700 text-white py-3.5 rounded-md text-sm font-semibold hover:bg-teal-800 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap">
                {isSubmitting ? 'Sending...' : 'Request Free Consultation'}
              </button>
              {status === 'success' && <div className="bg-teal-100 border border-teal-300 text-teal-800 px-4 py-3 rounded-md text-sm">Thank you! We'll be in touch shortly to schedule your free consultation.</div>}
              {status === 'error' && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">Something went wrong. Please call us directly at (201) 525-1155.</div>}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
